"use client";

// /community — the public commons: skills · guides · resources.
//
// The company page's grammar, worn by a place that belongs to everyone who
// visits: the section ticker, a dek that says plainly what this space is, a
// stat line carrying the `kept by` lens (one quiet dropdown, the house frosted
// menu), one row of category lenses, then a grid of frosted cards — cards
// only; there is no list view. Every card names its keeper — innernet, a
// member, or someone around the web — because the whole point of a public
// place is knowing whose position you're reading.
//
// Each space is its own ROOM (communityTheme.ts): its own pale ground and its
// own netti colour, built the house way from one hue, and the creature eases
// from one to the next as the ticker moves. Netti sits ABOVE the head's frosted
// band (community.css) so the band never frosts it; the words, chips and cards
// still sit above netti.
//
// The head is the stage's navigator (law_stage): the ticker, the dek, the stat
// line and the chips stand over the content, which scrolls in its own area under
// them. A room and a folder are PLACES: each is a history entry, and a move
// between them is a handoff in the direction of the ticker.
//
// Skills are read LIVE: a member's public skill is here because the door they
// opened is open, and gone the moment it isn't. The web's skills are ingested
// with attribution (scripts/ingest-web-skills.mjs): our link, their name on it,
// the source named on the card. Only the few whose licence forbids copying
// still open at the source.

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import BrandIcon from "../BrandIcon";
import OrganismBackdrop from "../OrganismBackdrop";
import SiteMenu, { MENU_ORG, MENU_ORG_SCALE } from "../SiteMenu";
import CompanyTicker from "../company/CompanyTicker";
import { useIsNarrow } from "../company/CompanyClient";
import { useCoTheme } from "../company/useCoTheme";
import { usePathname } from "next/navigation";
import OptionsMenu from "../os/artifact/OptionsMenu";
import type { MenuItem } from "../os/ContextMenu";
import { useNaturalHeight } from "../../lib/os/useNaturalHeight";
import Stage from "../stage/Stage";
import { useStageReadRamp } from "../stage/useStage";
import "../stage/surfaces/community.css";
import type { MenuSpace } from "../../lib/nav/siteNav";
import {
  COMMUNITY_DEKS,
  COMMUNITY_SPACES,
  communityHref,
  originName,
  type CommunitySkill,
  type CommunitySpace,
  type GuideMeta,
  type Resource,
} from "../../lib/community/types";
import { RESOURCES, resourceKinds } from "../../lib/community/resources";
import { CM_THEMES, NIGHT_SPACES, SKILLS_WASH } from "./communityTheme";
import { useRoomWash } from "./useRoomWash";
import { useChipRow } from "../os/chipRow";
import "./community.css";
import "./night.css";
import CommunitySearch from "./CommunitySearch";
import { Arrow, PackCard, SkillCard, Star, countUse } from "./SkillCard";
import PackRoom, { type PackView } from "./PackRoom";
import { findPack, packHref, packOfSkill, packsOf, type SkillPack } from "../../lib/community/packs";
import AboutBody from "./AboutBody";
import { ChapterCard, GuideCard } from "./GuideCards";
import { CHAPTERS } from "../../lib/community/chapters";
import { LinkBar } from "../skills/PasteUrl";
import { rank, termsOf } from "../../lib/community/search";
import { readerOrg, INDEX_ORG_WIDE, FULL_BLOOM_AGE } from "../../lib/os/readerOrganism";

type Keeper = "everyone" | "innernet" | "members" | "web";

const KEEPERS: { key: Keeper; label: string }[] = [
  { key: "everyone", label: "everyone" },
  { key: "innernet", label: "innernet" },
  { key: "members", label: "members" },
  { key: "web", label: "around the web" },
];

function keeperOf(s: CommunitySkill): Keeper {
  return s.origin.kind === "innernet" ? "innernet" : s.origin.kind === "member" ? "members" : "web";
}

/** the side a move between two rooms travels: +1 to the right of where you were,
 *  -1 to the left (wrapping reads the short way round, as the ticker does). */
function spaceDir(from: string, to: string): 1 | -1 {
  const delta = COMMUNITY_SPACES.findIndex((t) => t.key === to) - COMMUNITY_SPACES.findIndex((t) => t.key === from);
  return Math.abs(delta) === COMMUNITY_SPACES.length - 1 ? (delta > 0 ? -1 : 1) : Math.sign(delta) >= 0 ? 1 : -1;
}

/** the sort chip's arrow: down for newest first, turned over for oldest. */
function SortArrow({ up }: { up: boolean }) {
  return (
    <svg className="cm-sort__arrow" data-up={up || undefined} width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M6 2.2v7.4M2.9 6.6 6 9.7l3.1-3.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type Sort = "newest" | "oldest" | "top";

/** the same chevron the options trigger wears (ShareOptionsBar). */
function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 13 13" fill="none" aria-hidden>
      <path d="M3.25 5.1 6.5 8.35 9.75 5.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function CommunityClient({
  space: initialSpace,
  skills,
  guides,
  signedIn,
  memberName,
  origin,
}: {
  space: CommunitySpace;
  skills: CommunitySkill[];
  guides: GuideMeta[];
  signedIn: boolean;
  /** the visitor's own name, when signed in — so their own public skills read as "you". */
  memberName?: string | null;
  /** the site's origin, as the server writes links — so a link on the page
   *  renders the same on the server and in the browser */
  origin: string;
}) {
  const [space, setSpace] = useState<CommunitySpace>(initialSpace);
  const [lens, setLens] = useState<string | null>(null);
  const [keeper, setKeeper] = useState<Keeper>("everyone");
  // THE VIEW CHIP — sort lives inside the chip that used to say "all": it says
  // how the room is ordered, and pressing it again turns the order over.
  const [sort, setSort] = useState<Sort>("newest");
  // THE SEARCH — no dropdown: what is typed filters this room, live.
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const isNarrow = useIsNarrow();
  // on a phone the creature goes to the bottom centre and back UNDER the veil —
  // there is no field beside the words to stand in (lib/os/readerOrganism).
  const org = readerOrg(isNarrow, INDEX_ORG_WIDE);

  // the room — ground + netti for this space; the organism eases between them.
  // THE URL IS THE TRUTH (2026-09-20) — see the same note in CompanyClient.
  // A room switch writes the address with history.replaceState, so a link back
  // to an address we are "already on" (the crumb's `community`, the ≡ menu's
  // row, back/forward) used to move the address and leave the room standing.
  const pathname = usePathname();
  // A PACKAGE is a folder inside the skills room: /community/skills/<owner>/<repo>.
  // It is read off the address, so opening one is a pushState (no round trip)
  // and back/forward walk in and out of it like any room.
  const packs = useMemo(() => packsOf(skills), [skills]);
  const packParts = pathname.split("/").filter(Boolean);
  const pack: SkillPack | null =
    packParts.length === 4 && packParts[1] === "skills" ? findPack(packs, packParts[2], packParts[3]) : null;
  const [packView, setPackView] = useState<PackView>("folder");

  const theme = CM_THEMES[space];
  useCoTheme(theme.ground);
  const night = NIGHT_SPACES.has(space);
  useRoomWash(night ? SKILLS_WASH : null);

  // what the url says about THIS entry's lens, keeper, sort, search and folder
  // view: read on arrival, and again when Back or Forward lands on another entry
  // (a filter lives in the address of the place it was set in).
  const readQuery = () => {
    const q = new URLSearchParams(window.location.search);
    setLens(q.get("lens") || null);
    const by = q.get("by");
    setKeeper(by && KEEPERS.some((k) => k.key === by) ? (by as Keeper) : "everyone");
    const so = q.get("sort");
    setSort(so === "oldest" || so === "top" ? so : "newest");
    setQ((q.get("q") ?? "").slice(0, 80));
    setPackView(q.get("view") === "cards" ? "cards" : "folder");
  };
  // land on what the url asked for.
  useEffect(() => {
    readQuery();
  }, []);

  // law_read_ramp — the ground settles as you scroll to read. law_full_bloom (2026-09-26): netti here is always fully grown and sharp. The ramp only settles the GROUND behind it and the head's band; it never frosts, ages or rests the creature.
  // (on the stage: it hears the stage's scroller — components/stage/useStage)
  useStageReadRamp(220, "art");

  // law_stage — the head is the stage's navigator: the stage folds it (with the
  // scroll-up reveal); its folding half publishes its natural height.
  const foldRef = useRef<HTMLDivElement>(null);
  useNaturalHeight(foldRef, "--lock-h", `${space}:${pack?.key ?? ""}`);
  // the lenses are the one view-chip row (globals.css .nav-chips)
  const lensRow = useChipRow();
  // the side the chosen space sits on, relative to the current one (wrap short)
  const [navDir, setNavDir] = useState(1);

  // a ROOM is a place (it goes in the history, so back walks room to room); a
  // lens, a sort or a search term only re-addresses the room you are standing in.
  const syncUrl = (s: CommunitySpace, l: string | null, k: Keeper, so: Sort = sort, term: string = q, push = false) => {
    const params = new URLSearchParams();
    if (l) params.set("lens", l);
    if (k !== "everyone") params.set("by", k);
    if (so !== "newest") params.set("sort", so);
    if (term.trim()) params.set("q", term.trim());
    if (pack && s === "skills" && packView === "cards") params.set("view", "cards");
    const qs = params.toString();
    const url = `${pack && s === "skills" && !push ? packHref(pack) : communityHref(s)}${qs ? `?${qs}` : ""}`;
    // the address we are already on is not a new place
    if (url === window.location.pathname + window.location.search) return;
    if (push) window.history.pushState(null, "", url);
    else window.history.replaceState(null, "", url);
  };
  const selectSpace = (key: string, keepQuery = false) => {
    const s = key as CommunitySpace;
    setNavDir(spaceDir(space, s));
    setSpace(s);
    setLens(null);
    setKeeper("everyone");
    const so: Sort = sort === "top" && s !== "skills" ? "newest" : sort;
    setSort(so);
    if (!keepQuery) setQ("");
    syncUrl(s, null, "everyone", so, keepQuery ? q : "", true);
  };
  // BACK AND FORWARD walk the rooms and the folders (law_every_place_an_address):
  // the room, its folder and the entry's own filters come back with the address,
  // and the move hands the page over in the direction it travelled.
  const standing = useRef({ space, pack: null as string | null });
  useEffect(() => {
    standing.current = { space, pack: pack && space === "skills" ? pack.key : null };
  });
  useEffect(() => {
    const onPop = () => {
      const parts = window.location.pathname.split("/").filter(Boolean);
      if (parts[0] !== "community") return;
      const wanted = (parts.length === 1 ? "skills" : parts[1]) as CommunitySpace;
      if (!COMMUNITY_SPACES.some((t) => t.key === wanted)) return;
      const toPack = parts.length === 4 && parts[1] === "skills";
      const cur = standing.current;
      if (wanted !== cur.space) setNavDir(spaceDir(cur.space, wanted));
      else if (toPack !== !!cur.pack) setNavDir(toPack ? 1 : -1);
      readQuery();
      setSpace(wanted);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // …and an address that arrives some other way (the crumb's `community`, the ≡
  // menu's row) moves the room too: the URL is the truth, query and all.
  useEffect(() => {
    const parts = pathname.split("/").filter(Boolean);
    const wanted = (parts.length === 1 ? "skills" : parts[1]) as CommunitySpace;
    if (!COMMUNITY_SPACES.some((t) => t.key === wanted) || standing.current.space === wanted) return;
    setNavDir(spaceDir(standing.current.space, wanted));
    readQuery();
    setSpace(wanted);
  }, [pathname]);

  // a lens pressed twice lets go (there is no "all" chip to press any more).
  const selectLens = (l: string | null) => { const next = l === lens ? null : l; setLens(next); syncUrl(space, next, keeper); };
  const selectKeeper = (k: Keeper) => { setKeeper(k); syncUrl(space, lens, k); };
  // the sort chip: from `top` it comes back to newest; otherwise it turns over.
  const pressSort = () => { const next: Sort = sort === "newest" ? "oldest" : "newest"; setSort(next); syncUrl(space, lens, keeper, next); };
  const pressTop = () => { const next: Sort = sort === "top" ? "newest" : "top"; setSort(next); syncUrl(space, lens, keeper, next); };
  const type = (v: string) => { const t = v.slice(0, 80); setQ(t); syncUrl(space, lens, keeper, sort, t); };

  const openPack = (p: SkillPack, e?: React.MouseEvent) => {
    // a modified click (new tab, new window) keeps the ordinary link
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
    e?.preventDefault();
    setNavDir(1);
    setLens(null);
    setQ("");
    window.history.pushState(null, "", `${packHref(p)}${packView === "cards" ? "?view=cards" : ""}`);
    // (the stage opens a new place at its top)
  };

  // a folder's ONE link: every skill in it, whole, for an ai (lib/community/packDoc)
  const packLink = pack ? `${origin}${packHref(pack)}/raw` : "";
  const copyPack = async () => {
    if (!pack) return;
    try { await navigator.clipboard.writeText(packLink); } catch { return; }
    const key = `pack:${pack.key}`;
    setCopied(key);
    window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
  };

  const copyLink = async (s: CommunitySkill) => {
    try { await navigator.clipboard.writeText(s.pasteUrl); } catch { return; }
    setCopied(s.key);
    countUse(s.key, "copy");
    window.setTimeout(() => setCopied((c) => (c === s.key ? null : c)), 1600);
  };

  // the `kept by` dropdown — THE frosted menu, anchored to its trigger exactly
  // as `options ⌄` is (ShareOptionsBar.anchorOf); a bottom sheet on phones.
  const byRef = useRef<HTMLButtonElement>(null);
  const [byAnchor, setByAnchor] = useState<{ x: number; y: number; bottom: number } | null>(null);
  const toggleBy = () => {
    const r = byRef.current?.getBoundingClientRect();
    setByAnchor((a) => (a || !r ? null : { x: r.left, y: r.top, bottom: r.bottom }));
  };

  // ── the lists, lensed ────────────────────────────────────────────────────
  const skillLenses = useMemo(() => {
    const seen = new Map<string, number>();
    for (const s of skills) seen.set(s.category, (seen.get(s.category) ?? 0) + 1);
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [skills]);
  const keeperCounts = useMemo(() => {
    const c: Record<Keeper, number> = { everyone: skills.length, innernet: 0, members: 0, web: 0 };
    for (const s of skills) c[keeperOf(s)]++;
    return c;
  }, [skills]);
  // ── one search, three rooms ──────────────────────────────────────────────
  // The same words are ranked against every room (lib/community/search — the
  // ranking the door's innernet_find_skills uses too), so the page always knows
  // where the best answer lives, not only whether this room has one.
  const searching = termsOf(q).length > 0;
  const byDate = (a?: string, b?: string) => (a ?? "").localeCompare(b ?? "");
  const hitSkills = useMemo(
    () => rank(skills, q, (s) => ({ title: s.title, description: s.description, tags: s.tags, category: s.category, author: s.by ?? originName(s.origin), extra: s.when_to_load, weight: s.uses?.score })),
    [skills, q],
  );
  const hitGuides = useMemo(() => rank(guides, q, (g) => ({ title: g.title, description: g.description, category: g.category })), [guides, q]);
  const hitResources = useMemo(() => rank(RESOURCES, q, (r) => ({ title: r.name, description: r.description, category: r.kind })), [q]);

  const skillScore = useMemo(() => new Map(hitSkills.map((h) => [h.item.key, h.score])), [hitSkills]);
  const orderedSkills = useMemo(() => {
    const list = [...skills];
    if (searching) return list.sort((a, b) => (skillScore.get(b.key) ?? 0) - (skillScore.get(a.key) ?? 0));
    if (sort === "top") return list.sort((a, b) => (b.uses?.score ?? 0) - (a.uses?.score ?? 0) || byDate(b.updated, a.updated));
    // undated skills keep their place at the end whichever way the order runs
    return list.sort((a, b) => (!a.updated ? 1 : !b.updated ? -1 : sort === "newest" ? byDate(b.updated, a.updated) : byDate(a.updated, b.updated)));
  }, [skills, sort, searching, skillScore]);
  const inSkills = (s: CommunitySkill) =>
    (keeper === "everyone" || keeperOf(s) === keeper) && (!lens || s.category === lens) && (!searching || skillScore.has(s.key));
  const shownSkills = orderedSkills.filter(inSkills);
  // while searching, what does not match STAYS in the grid and fades out
  // (data-out) — the rest of the room leaves organically, it does not vanish.
  const gridSkills = searching ? orderedSkills.filter((s) => (keeper === "everyone" || keeperOf(s) === keeper) && (!lens || s.category === lens)) : shownSkills;
  // the shelf, folded: every skill that sits in a package is carried by ONE
  // folder card, placed where its first member would have been in the order.
  type GridItem = { kind: "skill"; skill: CommunitySkill } | { kind: "pack"; pack: SkillPack; members: CommunitySkill[] };
  const gridItems = useMemo(() => {
    const out: GridItem[] = [];
    const at = new Map<string, Extract<GridItem, { kind: "pack" }>>();
    for (const s of gridSkills) {
      const p = packOfSkill(s, packs);
      if (!p) { out.push({ kind: "skill", skill: s }); continue; }
      const held = at.get(p.key);
      if (held) { held.members.push(s); continue; }
      const item = { kind: "pack" as const, pack: p, members: [s] };
      at.set(p.key, item);
      out.push(item);
    }
    return out;
  }, [gridSkills, packs]);
  const packCount = gridItems.filter((g) => g.kind === "pack").length;
  // inside a folder, the search narrows the folder
  const packSkills = pack ? pack.skills.filter((s) => !searching || skillScore.has(s.key)) : [];

  const guideHit = useMemo(() => new Set(hitGuides.map((h) => h.item.slug)), [hitGuides]);
  const shownGuides = (searching ? hitGuides.map((h) => h.item) : [...guides].sort((a, b) => (sort === "oldest" ? byDate(a.updated, b.updated) : sort === "newest" && (a.updated || b.updated) ? byDate(b.updated, a.updated) : 0) || a.order - b.order))
    .filter((g) => (!lens || g.category === lens) && (!searching || guideHit.has(g.slug)));

  const resourceLenses = useMemo(() => resourceKinds(RESOURCES), []);
  const shownResources = (searching ? hitResources.map((h) => h.item) : RESOURCES).filter((r) => !lens || r.kind === lens);

  // where the best answer lives. A room wins when it holds the top-ranked
  // thing by a clear margin — not because it merely has a match.
  const best = useMemo(() => {
    if (!searching) return null;
    const tops: { space: CommunitySpace; score: number; title: string; n: number }[] = [
      { space: "skills", score: hitSkills[0]?.score ?? 0, title: hitSkills[0]?.item.title ?? "", n: hitSkills.length },
      { space: "guides", score: hitGuides[0]?.score ?? 0, title: hitGuides[0]?.item.title ?? "", n: hitGuides.length },
      { space: "resources", score: hitResources[0]?.score ?? 0, title: hitResources[0]?.item.name ?? "", n: hitResources.length },
      // about has nothing to list: searching from it always walks to where the answer is
      { space: "about", score: 0, title: "", n: 0 },
    ];
    const here = tops.find((t) => t.space === space)!;
    const other = tops.filter((t) => t.space !== space).sort((a, b) => b.score - a.score)[0];
    if (!other || other.score === 0) return null;
    if (here.score === 0) return { ...other, empty: true };
    return other.score >= here.score + 4 ? { ...other, empty: false } : null;
  }, [searching, hitSkills, hitGuides, hitResources, space]);

  // nothing here, something next door: go there, once the typing has settled.
  useEffect(() => {
    if (!best?.empty) return;
    const t = window.setTimeout(() => selectSpace(best.space, true), 700);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [best?.empty, best?.space]);

  const lenses = space === "skills" ? skillLenses : space === "resources" ? resourceLenses : [];
  const count = space === "skills" ? shownSkills.length : space === "guides" ? (searching ? shownGuides.length : CHAPTERS.filter((c) => guides.some((g) => g.chapter === c.key)).length) : shownResources.length;
  const noun = space === "skills" ? "skill" : space === "guides" ? (searching ? "guide" : "chapter") : "resource";

  const currentSpace: MenuSpace = {
    id: "community",
    label: "community",
    items: COMMUNITY_SPACES.map((t) => ({ key: t.key, label: t.label, here: t.key === space, onSelect: () => selectSpace(t.key) })),
  };

  const keeperItems: MenuItem[] = KEEPERS.map((k) => ({
    label: k.label,
    hint: String(keeperCounts[k.key]),
    selected: keeper === k.key,
    // the mark of the chosen row — passed as the icon so the phone's sheet
    // (which knows no `selected`) shows the same quiet dot.
    icon: keeper === k.key ? <span className="ink-dot" aria-hidden /> : undefined,
    disabled: k.key !== "everyone" && keeperCounts[k.key] === 0,
    onSelect: () => selectKeeper(k.key),
  }));


  return (
    <main className={`co-page cm-page${night ? " cm-night" : " cm-day"}`} style={{ ["--bg" as string]: theme.ground } as React.CSSProperties}>
      <OrganismBackdrop
        // law_focus_pull (2026-10-19): a page that is read rests netti out of focus
        readingFrost
        fixed
        anchor={menuOpen ? MENU_ORG.anchor : org.anchor}
        offset={menuOpen ? MENU_ORG.offset : org.offset}
        glide={menuOpen ? MENU_ORG.glide : undefined}
        glideAxis={menuOpen ? MENU_ORG.glideAxis : undefined}
        size="min(150vmin, 1520px)"
        blur={0}
        opacity={menuOpen ? MENU_ORG.opacity : 1}
        fade={0.9}
        scale={menuOpen ? (isNarrow ? MENU_ORG_SCALE.narrow : MENU_ORG_SCALE.wide) : 1}
        zIndex={menuOpen ? MENU_ORG.zIndex : org.zIndex}
        innerTransMs={menuOpen ? MENU_ORG.innerTransMs : undefined}
        innerEase={menuOpen ? MENU_ORG.innerEase : undefined}
        color={theme.color}
        age={FULL_BLOOM_AGE}
        spin
        publishHue
        live={false}
      />

      <div className="co-read-scrim" aria-hidden />
      {/* the head's frosted band, seated UNDER netti (community.css) */}

      <SiteMenu
        trail={
          pack && space === "skills"
            ? [{ label: "community", href: "/community" }, { label: "skills", href: "/community" }, { label: pack.title, truncate: true }]
            : [{ label: "community", href: "/community" }, ...(space === "skills" ? [] : [{ label: space }])]
        }
        signedIn={signedIn}
        current={currentSpace}
        hereKey="community"
        // "save it" only where there IS something to save — a skill you are
        // actually looking at. This is the shelf, not one of the things on it,
        // so the door here is the ordinary one.
        onOpenChange={setMenuOpen}
        search={<CommunitySearch value={q} onChange={type} onEnter={() => best && selectSpace(best.space, true)} label={`search ${space}`} />}
      />

      {/* law_stage — the head is the stage's navigator, over the top of the
          content's own scroll area; the stage folds it (the ticker and the chips
          stay, the dek and the stat line fold away), reveals it on a scroll up,
          and hands each room and folder over in the direction of the ticker */}
      <Stage
        frame="none"
        autoFold
        top="clamp(4.6rem, 11vh, 6.4rem)"
        pin="clamp(4.2rem, 8.6vh, 4.75rem)"
        gap="clamp(1.6rem, 4vh, 2.6rem)"
        headClassName="os-lock--detached"
        sceneKey={pack && space === "skills" ? `pack:${pack.key}` : space}
        dir={navDir >= 0 ? 1 : -1}
        swipe
        label={pack && space === "skills" ? pack.title : space}
        rootProps={{ className: "stage--community" }}
        head={
          <div className="co-shell cm-shell stage-community__head">
            <header className="co-head co-head--menu">
              <CompanyTicker tabs={COMMUNITY_SPACES} active={space} onSelect={selectSpace} />
              <div ref={foldRef} className="os-lock__fold co-head__fold">
                {pack && space === "skills" ? (
                  <>
                    <p className="co-dek cm-dek cm-pack-head">
                      <span className="cm-pack-head__title">{pack.title}</span>
                      <span className="cm-pack-head__d">{pack.description}</span>
                    </p>
                    <div className="co-meta cm-meta">
                      <span>{pack.skills.length} skills in this folder</span>
                      <span className="cm-meta__sep" aria-hidden>·</span>
                      <span>kept by <b className="cm-pack-head__who">{originName(pack.origin)}</b></span>
                      {pack.broughtBy && (
                        <>
                          <span className="cm-meta__sep" aria-hidden>·</span>
                          <span>brought by {memberName && pack.broughtBy.name === memberName ? "you" : pack.broughtBy.name}</span>
                        </>
                      )}
                      <span className="cm-meta__sep" aria-hidden>·</span>
                      <a className="quiet-link" href={pack.url} target="_blank" rel="noopener noreferrer">github.com/{pack.owner}/{pack.repo}</a>
                    </div>
                    {/* THE PASTE LINE, one level up: the whole folder in one link */}
                    <div className="sk-paste cm-pack-paste">
                      <p className="sk-paste-say">
                        paste this link into your ai. it brings all {pack.skills.length} skills in this folder at once.
                      </p>
                      <LinkBar url={packLink} done={copied === `pack:${pack.key}`} onCopy={copyPack} />
                    </div>
                  </>
                ) : (<>
                <p className="co-dek cm-dek">{COMMUNITY_DEKS[space]}</p>
                {space === "skills" && (
                  <p className="cm-say">
                    anyone with an innernet can keep skills here. share your own, or hand your ai a post where someone
                    shared theirs. it lands here under <em>their</em> name, with yours beside it.
                    {" "}<Link className="quiet-link" href="/skills/bring-a-skill">how to bring one</Link>
                    {signedIn ? (
                      <> · <Link className="quiet-link" href="/dashboard">open yours</Link></>
                    ) : (
                      <> · <Link className="quiet-link" href="/signup?from=community">start one</Link></>
                    )}
                  </p>
                )}
                {space !== "about" && <div className="co-meta cm-meta">
                  <span>{count} {count === 1 ? noun : `${noun}s`}</span>
                  {space === "skills" && (
                    <>
                      <span className="cm-meta__sep" aria-hidden>·</span>
                      <button
                        ref={byRef}
                        type="button"
                        className="os-link cm-by"
                        aria-haspopup="menu"
                        aria-expanded={!!byAnchor}
                        onClick={toggleBy}
                      >
                        <span className="os-link-text">kept by <b>{KEEPERS.find((k) => k.key === keeper)?.label}</b></span>
                        <Chevron />
                      </button>
                      {packCount > 0 && (
                        <>
                          <span className="cm-meta__sep" aria-hidden>·</span>
                          <span>{packCount} {packCount === 1 ? "folder" : "folders"}</span>
                        </>
                      )}
                    </>
                  )}
                </div>}
                </>)}
              </div>

              {space !== "about" && <div className="co-chips cm-chips">
                <div className="cm-search-narrow">
                  <CommunitySearch face="field" value={q} onChange={type} onEnter={() => best && selectSpace(best.space, true)} label={`search ${space}`} />
                </div>
                {pack && space === "skills" ? null : (
                <div ref={lensRow} className="cm-lenses nav-chips" role="group" aria-label={`lenses on ${space}`}>
                  {space === "skills" && (
                    <button
                      type="button"
                      className="chip cm-sort"
                      data-on={sort !== "top" ? "true" : undefined}
                      onClick={sort === "top" ? pressTop : pressSort}
                      aria-label={sort === "oldest" ? "sorted oldest first. press for newest first" : "sorted newest first. press for oldest first"}
                    >
                      {sort === "oldest" ? "oldest" : "newest"}
                      <SortArrow up={sort === "oldest"} />
                    </button>
                  )}
                  {space === "skills" && (
                    <button type="button" className="chip cm-sort" data-on={sort === "top" ? "true" : undefined} aria-pressed={sort === "top"} onClick={pressTop}>
                      <Star />
                      top
                    </button>
                  )}
                  {space === "skills" && lenses.length > 0 && <span className="cm-lenses__sep" aria-hidden />}
                  {lenses.map((l) => (
                    <button key={l} type="button" className="chip" data-on={lens === l ? "true" : undefined} aria-pressed={lens === l} onClick={() => selectLens(l)}>
                      {l}
                    </button>
                  ))}
                </div>
                )}
                {best && !best.empty && (
                  <button type="button" className="cm-elsewhere quiet-link" onClick={() => selectSpace(best.space, true)}>
                    the closest match is in {best.space}: {best.title}
                  </button>
                )}
              </div>}
            </header>
          </div>
        }
      >
        {/* the room's content arrives from the side its word was on (the Stage
            keys and slides the scene: the same swipe the dashboard and docs play) */}
        <div className="co-shell cm-shell stage-community__body">
          <div className="co-body cm-body">
            {space === "about" && <AboutBody />}

            {/* ── skills ─────────────────────────────────────────────────── */}
            {space === "skills" && pack && (
              <PackRoom key={pack.key} pack={pack} view={packView} skills={packSkills} copied={copied} onCopy={(s) => void copyLink(s)} memberName={memberName} />
            )}
            {space === "skills" && !pack && (shownSkills.length === 0 ? (
              <p className="co-empty">
                {keeper === "members"
                  ? "no member has published a skill yet. share one for anyone with the link and yours will be the first here."
                  : searching ? `nothing on the shelf matches "${q.trim()}" yet.` : "nothing under this lens yet."}
              </p>
            ) : (
              <div className="cm-grid" data-searching={searching || undefined}>
                {gridItems.map((g, i) => {
                  const enter = i < 12 && !searching ? i : undefined;
                  if (g.kind === "pack") {
                    const hit = g.members.filter((m) => !searching || skillScore.has(m.key));
                    return (
                      <PackCard
                        key={`pack:${g.pack.key}`}
                        pack={g.pack}
                        members={hit}
                        onOpen={openPack}
                        memberName={memberName}
                        enter={enter}
                        out={searching && hit.length === 0}
                        filtered={searching || !!lens || keeper !== "everyone"}
                      />
                    );
                  }
                  const s = g.skill;
                  return (
                    <SkillCard
                      key={s.key}
                      skill={s}
                      copied={copied === s.key}
                      onCopy={(x) => void copyLink(x)}
                      memberName={memberName}
                      enter={enter}
                      out={searching && !skillScore.has(s.key)}
                    />
                  );
                })}
              </div>
            ))}

            {/* ── guides ─────────────────────────────────────────────────── */}
            {space === "guides" && (shownGuides.length === 0 ? (
              <p className="co-empty">nothing in the guides matches that yet.</p>
            ) : searching ? (
              // while searching, the guides themselves, ranked
              <div className="cm-grid">
                {shownGuides.map((g, i) => (
                  <GuideCard key={g.slug} guide={g} of={guides.filter((x) => x.chapter === g.chapter).length || undefined} enter={i < 12 ? i : undefined} />
                ))}
              </div>
            ) : (
              // THE CHAPTERS (2026-09-29): the guides are read as a handful of
              // folders, never a flat shelf. Each opens into its deck of steps.
              <div className="cm-grid gd-chapters">
                {CHAPTERS.map((c, i) => {
                  const gs = guides.filter((g) => g.chapter === c.key).sort((a, b) => (a.step ?? 99) - (b.step ?? 99));
                  return gs.length ? <ChapterCard key={c.key} chapter={c} guides={gs} enter={i} /> : null;
                })}
              </div>
            ))}

            {/* ── resources ──────────────────────────────────────────────── */}
            {space === "resources" && (
              <div className="cm-grid cm-grid--res">
                {shownResources.map((r: Resource, i) => {
                  const ext = /^https?:/.test(r.url);
                  const Tag = ext ? "a" : Link;
                  return (
                    <Tag key={r.key} className={i < 12 ? "cm-card cm-card--res cm-enter" : "cm-card cm-card--res"} style={i < 12 ? ({ ["--i" as string]: i } as React.CSSProperties) : undefined} href={r.url} {...(ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                      <span className="cm-card__top">
                        <span className="cm-res__mark"><BrandIcon name={r.brand ?? r.name} size={22} /></span>
                        <span className="cm-card__cat">{r.kind}</span>
                        {ext && <span className="cm-card__ext"><Arrow /></span>}
                      </span>
                      <span className="cm-card__t cm-card__t--res">{r.name}</span>
                      <span className="cm-card__d">{r.description}</span>
                      {r.connects && (
                        <span className="cm-card__by">
                          <span className="ink-dot" aria-hidden />
                          <span className="cm-card__keeper">connects to innernet</span>
                        </span>
                      )}
                    </Tag>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Stage>

      <OptionsMenu open={!!byAnchor} onClose={() => setByAnchor(null)} items={keeperItems} anchor={byAnchor} title="kept by" />
    </main>
  );
}
